
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  InputAdornment,
  TablePagination,
  TableSortLabel
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Plus as PlusIcon, RefreshCw, Search as SearchIcon, Tag as CategoryLogoIcon } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';

const StandardBadge = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 36,
  height: 36,
  padding: '0 8px',
  borderRadius: 6,
  backgroundColor: 'rgba(255, 171, 0, 0.16)',
  color: 'rgb(183, 110, 0)',
  fontSize: '0.85rem',
  fontWeight: 700,
}));

export default function CategorieModulistica() {
  const [categorie, setCategorie] = useState([]);
  const [filteredCategorie, setFilteredCategorie] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentCategoria, setCurrentCategoria] = useState(null);
  const [formData, setFormData] = useState({ nome_categoria: '' });
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('nome_categoria');
  const [order, setOrder] = useState('asc');

  useEffect(() => {
    fetchCategorie();
  }, []);

  useEffect(() => {
    filterAndSortCategorie();
  }, [categorie, searchTerm, orderBy, order]);

  const fetchCategorie = async () => {
    setLoading(true);
    try {
      const response = await api.get('/categorie-utilizzo');
      setCategorie(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load data');
      toast.error('Errore nel caricamento delle categorie');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortCategorie = () => {
    let result = [...categorie];

    // Filter
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter(item => 
        (item.nome_categoria && item.nome_categoria.toLowerCase().includes(lowerSearch))
      );
    }

    // Sort
    result.sort((a, b) => {
      let valueA = a[orderBy];
      let valueB = b[orderBy];

      // Handle null/undefined
      if (valueA === null || valueA === undefined) valueA = '';
      if (valueB === null || valueB === undefined) valueB = '';

      if (orderBy === 'id') {
        valueA = Number(valueA);
        valueB = Number(valueB);
      }

      if (valueA < valueB) return order === 'asc' ? -1 : 1;
      if (valueA > valueB) return order === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredCategorie(result);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (categoria = null) => {
    if (categoria) {
      setCurrentCategoria(categoria);
      setFormData({ nome_categoria: categoria.nome_categoria });
    } else {
      setCurrentCategoria(null);
      setFormData({ nome_categoria: '' });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentCategoria(null);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.nome_categoria) {
        toast.error('Il nome della categoria è obbligatorio');
        return;
      }

      if (currentCategoria) {
        await api.put(`/categorie-utilizzo/${currentCategoria.id}`, formData);
        toast.success('Categoria aggiornata con successo');
      } else {
        await api.post('/categorie-utilizzo', formData);
        toast.success('Categoria creata con successo');
      }
      
      fetchCategorie();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving category:', err);
      const message = err.response?.data?.msg || 'Errore nel salvataggio';
      setError(message);
      toast.error(message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Sei sicuro di voler eliminare questa categoria?')) {
      try {
        await api.delete(`/categorie-utilizzo/${id}`);
        toast.success('Categoria eliminata');
        fetchCategorie();
      } catch (err) {
        console.error('Error deleting category:', err);
        toast.error('Errore durante l\'eliminazione');
      }
    }
  };

  // Pagination calculation
  const paginatedCategorie = filteredCategorie.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50">
            <CategoryLogoIcon className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <Typography variant="h4" className="font-bold text-gray-800 dark:text-gray-100">
              Categorie Modulistica
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Gestisci le categorie per i modelli di documento
            </Typography>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outlined"
            size="small"
            onClick={fetchCategorie}
            title="Ricarica"
          >
            <RefreshCw size={16} />
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => handleOpenDialog()}
          >
            <PlusIcon size={16} style={{ marginRight: 8 }} />
            Nuova Categoria
          </Button>
        </div>
      </div>

      {error && <Alert severity="error">{error}</Alert>}

      <Card className="shadow-md border-none overflow-hidden rounded-xl">
        <Box className="p-4 border-b border-gray-100">
          <TextField
            placeholder="Cerca per nome categoria..."
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon className="h-4 w-4 text-gray-400" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'background.neutral' }}>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'nome_categoria'}
                    direction={orderBy === 'nome_categoria' ? order : 'asc'}
                    onClick={() => handleRequestSort('nome_categoria')}
                  >
                    Nome Categoria
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'total_modelli'}
                    direction={orderBy === 'total_modelli' ? order : 'asc'}
                    onClick={() => handleRequestSort('total_modelli')}
                  >
                    Totale Modelli
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  % sul totale
                </TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" className="py-8">
                    <RefreshCw className="animate-spin h-6 w-6 text-gray-400 mx-auto" />
                  </TableCell>
                </TableRow>
              ) : filteredCategorie.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" className="py-8 text-gray-500">
                    Nessuna categoria trovata
                  </TableCell>
                </TableRow>
              ) : (
                paginatedCategorie.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell className="font-medium">{row.nome_categoria}</TableCell>
                    <TableCell align="center">
                      <StandardBadge>
                        {row.total_modelli || 0}
                      </StandardBadge>
                    </TableCell>
                    <TableCell align="center">
                      {parseFloat(row.percentage || 0) > 0 ? (
                        <div style={{ position: 'relative', display: 'inline-flex' }}>
                          <CircularProgress
                            variant="determinate"
                            value={100}
                            size={36}
                            thickness={6}
                            sx={{ color: 'rgba(145, 158, 171, 0.24)' }}
                          />
                          <CircularProgress
                            variant="determinate"
                            value={parseFloat(row.percentage || 0)}
                            size={36}
                            thickness={6}
                            sx={{ position: 'absolute', left: 0, color: 'rgb(255, 171, 0)' }}
                          />
                          <Box
                            sx={{
                              top: 0,
                              left: 0,
                              bottom: 0,
                              right: 0,
                              position: 'absolute',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.65rem' }}>
                              {parseFloat(row.percentage || 0).toFixed(0)}%
                            </Typography>
                          </Box>
                        </div>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(row)}
                        sx={{ color: 'text.primary' }}
                      >
                        <EditIcon size={18} />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(row.id)} color="error">
                        <DeleteIcon size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredCategorie.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </Card>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {currentCategoria ? 'Modifica Categoria' : 'Nuova Categoria'}
        </DialogTitle>
        <DialogContent dividers className="space-y-4">
          <TextField
            autoFocus
            margin="dense"
            name="nome_categoria"
            label="Nome Categoria"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.nome_categoria}
            onChange={handleChange}
          />
        </DialogContent>
        <DialogActions className="p-4">
          <Button
            variant="outlined"
            onClick={handleCloseDialog}
          >
            Annulla
          </Button>
          <Button variant="contained" onClick={handleSubmit}>
            Salva
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
